import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from './api';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/image-availability-exceptions`;

export type ImageExceptions = {
  _id?: string;
  image: string;
  siteCode?: string;
  targetUrl?: string;
  from?: string;
  to?: string;
  createdAt?: string;
  updatedAt?: string;
};

type GetImageExceptionsResponse = {
  items: ImageExceptions[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  lastUpdated?: string;
};

export type BulkOperationsPayload = {
  create?: Omit<ImageExceptions, '_id' | 'createdAt' | 'updatedAt'>[];
  delete?: string[];
};

export type BulkOperationsResponse = {
  message: string;
  result: {
    created: number;
    deleted: number;
    totalOperations: number;
  };
  data: {
    created: ImageExceptions[];
    deleted: string[];
  };
};

export type HistoryRecord = {
  _id: string;
  image: string;
  siteCode?: string;
  targetUrl?: string;
  from?: string;
  to?: string;
  operation: 'add' | 'remove';
  userEmail: string;
  createdAt: string;
};

export type HistoryResponse = {
  items: HistoryRecord[];
  totalCount: number;
  totalPages: number;
};

export const apiImageExceptions = createApi({
  reducerPath: 'apiImageExceptions',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['imageExceptions', 'imageExceptionsOperationsHistory'],
  endpoints: (build) => ({
    getImageExceptions: build.query<GetImageExceptionsResponse, { page?: number; limit?: number }>({
      query: ({ page = 1, limit }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        if (limit !== undefined) {
          params.append('limit', limit.toString());
        }

        return {
          url: `/?${params.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['imageExceptions']
    }),
    bulkUpdateImageExceptions: build.mutation<BulkOperationsResponse, BulkOperationsPayload>({
      query: (body) => ({
        url: '/bulk-operations',
        method: 'POST',
        body
      }),
      invalidatesTags: ['imageExceptions', 'imageExceptionsOperationsHistory']
    }),
    getHistory: build.query<
      HistoryResponse,
      { page?: number; limit?: number; fromDate?: string; toDate?: string; image?: string; userEmail?: string }
    >({
      query: ({ page = 1, limit = 10, fromDate, toDate, image, userEmail }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString()
        });

        if (fromDate) {
          params.append('fromDate', fromDate);
        }
        if (toDate) {
          params.append('toDate', toDate);
        }
        if (image) {
          params.append('image', image);
        }
        if (userEmail) {
          params.append('userEmail', userEmail);
        }

        return {
          url: `/history?${params.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['imageExceptionsOperationsHistory']
    }),
    getUserEmails: build.query<string[], void>({
      query: () => ({
        url: '/history/users',
        method: 'GET'
      }),
      providesTags: ['imageExceptionsOperationsHistory']
    })
  })
});

export const {
  useGetImageExceptionsQuery,
  useLazyGetImageExceptionsQuery,
  useBulkUpdateImageExceptionsMutation,
  useGetHistoryQuery,
  useGetUserEmailsQuery
} = apiImageExceptions;
